import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLastPageView } from "@/core/modules/tracking/use-last-page-view";
import { useCreateVerificationSession } from "../api/create-verification-session";
import { type PatientResponse, usePatientAuthentication } from "../api/patient-authentication";
import { useResendVerificationCode } from "../api/resend-verification-code";
import { useVerifyPhone } from "../api/verify-phone";
import {
  clearStoredVerificationToken,
  readStoredVerificationToken,
  writeStoredVerificationToken,
} from "../lib/verification-token-storage";
import { useCustomerDataState } from "../store/use-customer-data-state";
import { useVouchersDistributionState } from "../store/use-vouchers-distribution-state";
import {
  safeTrackEvent,
  signUpViewEvent,
  verificationErrorEvent,
  verificationResendCodeEvent,
  verificationSuccessEvent,
  verificationViewEvent,
} from "../tracking/events";
import type { ConfigName } from "../types/config-name";
import { extractErrorCode, isOtpErrorCode } from "../utils/extract-error-code";
import { readGaUserType, writeGaUserType } from "../utils/ga-user-type";

export type PhoneCheckoutStep =
  | "PhoneInput"
  | "VerifyingPhone"
  | "NewUser"
  | "NewUserOTPOverlay"
  | "HasAccountOTP"
  | "DataLoaded";

export type UserData = {
  firstName: string;
  lastName: string;
  email: string;
  acceptNewsletter: boolean;
  acceptMarketingPhoneContact: boolean;
};

type UsePhoneCheckoutFlowOptions = {
  configName: ConfigName;
  // Discriminates the new-user OTP overlay's verification events: "guest" for simplified/draft
  // registration (no password), "sign_up" for full registration. Defaults to "sign_up" because the
  // password-bearing path is the dominant one — distributions without simplifiedRegistrationAvailable
  // can omit this prop entirely. Read fresh on each event firing so toggling guest checkout mid-form
  // (the PatientForm "kontynuuj jako gość" CTA) flips subsequent verification events accordingly.
  isGuestCheckout?: boolean;
};

const mapPatientToUserData = (patient: PatientResponse): UserData => ({
  firstName: patient.firstName,
  lastName: patient.lastName,
  email: patient.email,
  // Coerce null → false for consent fields. Backend can send null on legacy
  // accounts that never had the prompt; the form schema (yup boolean()) rejects
  // null and surfaces "must be a `boolean` type" inline on the consent checkbox.
  acceptNewsletter: patient.acceptNewsletter ?? false,
  acceptMarketingPhoneContact: patient.acceptMarketingPhoneContact ?? false,
});

export const usePhoneCheckoutFlow = ({ configName, isGuestCheckout = false }: UsePhoneCheckoutFlowOptions) => {
  const { t } = useTranslation();
  const { voucherDistribution } = useVouchersDistributionState();
  const { setCustomerData } = useCustomerDataState();
  const { getLastPageView, setLastPageView } = useLastPageView();

  const translateError = useCallback(
    (error: unknown, namespace: "PHONE_AUTH" | "OTP", fallbackKey: string) => {
      const code = extractErrorCode(error);
      if (code) {
        const key = `VOUCHERS_DISTRIBUTION.ERRORS.${namespace}.${code.toUpperCase()}`;
        const translated = t(key);
        if (translated && translated !== key) return translated;
      }
      return t(fallbackKey);
    },
    [t],
  );

  const [step, setStep] = useState<PhoneCheckoutStep>("PhoneInput");
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [pendingVerificationCode, setPendingVerificationCode] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [currentPhone, setCurrentPhone] = useState<{ dialCode: string; phone: string } | null>(null);

  const [otpResendCount, setOtpResendCount] = useState(0);
  const [otpCooldownEnd, setOtpCooldownEnd] = useState<number | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isExistingVerificationSession, setIsExistingVerificationSession] = useState(false);
  // Backend reports `not_found` when the underlying TFA session is gone — the OTP form
  // is dead at that point (token cleared just below, retyping the code can't recover).
  // The form reads this flag to lock the input and swap its CTA to a "try again" affordance
  // that re-runs phone verification (instead of letting the user keep typing into nothing).
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  // Bumped whenever a submitted OTP is rejected, so CheckoutOtpForm can wipe its
  // input without us having to lift its `code` state up here.
  const [otpInputResetSignal, setOtpInputResetSignal] = useState(0);
  // Dedupes `sign_up_view` within a single "form session": entering NewUser fires once;
  // toggling activeCard tabs or bouncing through NewUserOTPOverlay does not re-fire; only
  // changePhone() (user backed out to PhoneInput) resets it so a fresh form-landing fires again.
  const signUpViewFiredRef = useRef(false);

  const { mutate: verifyPhone, isLoading: isVerifyPhoneLoading } = useVerifyPhone();
  const { mutate: patientAuthenticate, isLoading: isPatientAuthenticationLoading } = usePatientAuthentication();
  const { mutate: resendVerificationCode, isLoading: isResendOtpLoading } = useResendVerificationCode({});
  const { mutate: createVerificationSession, isLoading: isCreateVerificationSessionLoading } =
    useCreateVerificationSession();

  const startOtpCooldown = useCallback(() => {
    setOtpCooldownEnd(Date.now() + 30000);
  }, []);

  const submitPhone = useCallback(
    (dialCode: string, phone: string) => {
      if (!voucherDistribution?.slug) return;
      const slug = voucherDistribution.slug;
      setPhoneError(null);
      setIsExistingVerificationSession(false);
      setIsSessionExpired(false);
      setCurrentPhone({ dialCode, phone });
      setStep("VerifyingPhone");
      verifyPhone(
        {
          slug,
          configName,
          dialCode,
          phone,
        },
        {
          onSuccess: (data) => {
            switch (data.status) {
              case "patient_session_already_active":
                setUserData(mapPatientToUserData(data.patient));
                setStep("DataLoaded");
                // REV-168: backend skipped OTP because the patient session is still valid
                // (e.g. previous registration in the same browser). Without firing
                // verification_success the funnel sees the user "magically" reach checkout
                // and add_payment_info.user_type stays null. Guarded on readGaUserType()
                // so this never overwrites a fresh "new_registered" / "guest" set by
                // registerPatient.onSuccess earlier in the same session.
                if (!readGaUserType()) {
                  const patientUuid = data.patient?.uuid ?? null;
                  // REV-170: propagate the recovered uuid to lastPageView so the next page_view
                  // carries user_id (mirrors GAO use-login).
                  // REV-211: same for user_ordered_visits (?? 0 — null for restricted_app_access).
                  const userOrderedVisits = data.patient?.visitsCount ?? 0;
                  const lastPageView = getLastPageView();
                  if (lastPageView) {
                    setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
                  }
                  safeTrackEvent(
                    () => verificationSuccessEvent({ userId: patientUuid, method: "account" }),
                    "verification_success",
                    "use-phone-checkout-flow submitPhone patient_session_already_active",
                  );
                  writeGaUserType("existing");
                }
                break;
              case "verification_required":
                writeStoredVerificationToken(slug, dialCode, phone, data.token);
                setVerificationToken(data.token);
                setStep("HasAccountOTP");
                startOtpCooldown();
                safeTrackEvent(
                  () => verificationViewEvent({ method: "account" }),
                  "verification_view",
                  "use-phone-checkout-flow submitPhone verification_required",
                );
                break;
              case "verification_session_already_active": {
                // Backend has an open 2FA session for this phone — SMS was already sent earlier
                // and no new one is dispatched. Backend currently doesn't return a token in
                // this branch, so we replay the one we stored from the original verification_required
                // response. The OTP form is shown either way: the banner copy ("…or try again in
                // a moment") tells the user to wait for the session to expire if they can't find
                // the SMS, and `submitOtp` no-ops when the token is missing.
                const recoveredToken = data.token ?? readStoredVerificationToken(slug, dialCode, phone);
                if (recoveredToken) {
                  writeStoredVerificationToken(slug, dialCode, phone, recoveredToken);
                }
                setVerificationToken(recoveredToken);
                setIsExistingVerificationSession(true);
                setStep("HasAccountOTP");
                // Intentionally no startOtpCooldown — no fresh SMS was sent.
                safeTrackEvent(
                  () => verificationViewEvent({ method: "account" }),
                  "verification_view",
                  "use-phone-checkout-flow submitPhone verification_session_already_active",
                );
                break;
              }
              case "registration_required":
                // All distributions defer OTP until after the registration form is submitted.
                // The verification session is opened on form submit via triggerNewUserOtpOverlay
                // (POST /verification_sessions), which dispatches the SMS and starts cooldown.
                setStep("NewUser");
                if (!signUpViewFiredRef.current) {
                  signUpViewFiredRef.current = true;
                  safeTrackEvent(
                    () => signUpViewEvent(),
                    "sign_up_view",
                    "use-phone-checkout-flow submitPhone registration_required",
                  );
                }
                break;
            }
          },
          onError: (error) => {
            // Drop the in-flight phone snapshot so the parent's controlled input
            // (value falls through to local state when currentPhone is null)
            // becomes editable again — otherwise it stays visually pinned to the
            // submitted value despite step being back on PhoneInput.
            setCurrentPhone(null);
            setStep("PhoneInput");
            setPhoneError(translateError(error, "PHONE_AUTH", "VOUCHERS_DISTRIBUTION.ERRORS.PHONE_AUTH.GENERAL_ERROR"));
          },
        },
      );
    },
    [
      voucherDistribution?.slug,
      configName,
      verifyPhone,
      startOtpCooldown,
      translateError,
      getLastPageView,
      setLastPageView,
    ],
  );

  const submitOtp = useCallback(
    (code: string) => {
      if (!voucherDistribution?.slug) return;
      if (!verificationToken) {
        // Backend session is open but we never received its token (page opened in a new
        // tab / different device / storage cleared). The form is shown so the user sees
        // the existing-session banner, but submission has nothing to send. Surface a
        // visible error instead of silently swallowing the click.
        setOtpError(t("VOUCHERS_DISTRIBUTION.ERRORS.OTP.SESSION_NOT_RECOVERABLE"));
        return;
      }
      const slug = voucherDistribution.slug;
      setOtpError(null);
      patientAuthenticate(
        {
          slug,
          configName,
          token: verificationToken,
          code,
        },
        {
          onSuccess: (data) => {
            // Token is consumed server-side; drop the stored copy so a future
            // visit doesn't replay it.
            if (currentPhone) {
              clearStoredVerificationToken(slug, currentPhone.dialCode, currentPhone.phone);
            }
            setUserData(mapPatientToUserData(data.patient));
            setStep("DataLoaded");
            setOtpError(null);
            const patientUuid = data.patient?.uuid ?? null;
            // REV-170: propagate the just-authenticated patient uuid to lastPageView so the next
            // page_view (e.g. side-cart-driven submit triggering navigation) carries user_id.
            // Mirrors GAO use-login.
            // REV-211: same for user_ordered_visits (?? 0 — null for restricted_app_access).
            const userOrderedVisits = data.patient?.visitsCount ?? 0;
            const lastPageView = getLastPageView();
            if (lastPageView) {
              setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
            }
            safeTrackEvent(
              () => verificationSuccessEvent({ userId: patientUuid, method: "account" }),
              "verification_success",
              "use-phone-checkout-flow submitOtp onSuccess",
            );
            writeGaUserType("existing");
          },
          onError: (error) => {
            const code = extractErrorCode(error);
            // Dead-session signals: the stored token can never authenticate again, so
            // drop it to prevent the OTP form from re-opening with a stale token after
            // the user clicks "Zmień numer" and re-submits the same phone.
            if ((code === "code_expired" || code === "not_found") && currentPhone) {
              clearStoredVerificationToken(slug, currentPhone.dialCode, currentPhone.phone);
              setVerificationToken(null);
            }
            if (code === "not_found") {
              setIsSessionExpired(true);
            }
            setOtpError(translateError(error, "OTP", "VOUCHERS_DISTRIBUTION.ERRORS.OTP.INVALID_CODE"));
            setOtpInputResetSignal((s) => s + 1);
            safeTrackEvent(
              () => verificationErrorEvent({ errorType: code ?? "unknown", method: "account" }),
              "verification_error",
              "use-phone-checkout-flow submitOtp onError",
            );
          },
        },
      );
    },
    [
      voucherDistribution?.slug,
      configName,
      verificationToken,
      currentPhone,
      patientAuthenticate,
      translateError,
      t,
      getLastPageView,
      setLastPageView,
    ],
  );

  const triggerNewUserOtpOverlay = useCallback(() => {
    if (!voucherDistribution?.slug || !currentPhone) return;
    const slug = voucherDistribution.slug;
    const { dialCode, phone } = currentPhone;
    setOtpError(null);
    setPendingVerificationCode(null);
    setIsExistingVerificationSession(false);

    createVerificationSession(
      {
        slug,
        configName,
        dialCode,
        phone,
      },
      {
        onSuccess: (data) => {
          // Persist the token so a later `verification_session_already_active` 409
          // (e.g. user closed the overlay and re-submitted the form) can replay it.
          writeStoredVerificationToken(slug, dialCode, phone, data.token);
          setVerificationToken(data.token);
          setStep("NewUserOTPOverlay");
          startOtpCooldown();
          safeTrackEvent(
            () => verificationViewEvent({ method: isGuestCheckout ? "guest" : "sign_up" }),
            "verification_view",
            "use-phone-checkout-flow triggerNewUserOtpOverlay onSuccess",
          );
        },
        onError: (error) => {
          if (extractErrorCode(error) === "verification_session_already_active") {
            // Backend already has an open 2FA session for this phone — SMS was sent
            // earlier, no fresh one is dispatched. Open the overlay so the user can
            // type the code they already received; the banner copy also tells them
            // to wait if they can't find it.
            const recoveredToken = readStoredVerificationToken(slug, dialCode, phone);
            setVerificationToken(recoveredToken);
            setIsExistingVerificationSession(true);
            setStep("NewUserOTPOverlay");
            // Intentionally no startOtpCooldown — no fresh SMS was sent.
            safeTrackEvent(
              () => verificationViewEvent({ method: isGuestCheckout ? "guest" : "sign_up" }),
              "verification_view",
              "use-phone-checkout-flow triggerNewUserOtpOverlay session_already_active",
            );
            return;
          }
          toast.error(translateError(error, "PHONE_AUTH", "VOUCHERS_DISTRIBUTION.ERRORS.PHONE_AUTH.GENERAL_ERROR"));
        },
      },
    );
  }, [
    voucherDistribution?.slug,
    configName,
    currentPhone,
    createVerificationSession,
    startOtpCooldown,
    translateError,
    isGuestCheckout,
  ]);

  const preflightVerifyForSubmit = useCallback(
    ({ onSubmit }: { onSubmit: () => void }) => {
      // Re-runs phone_verifications right before the form is submitted, so the
      // backend gets the final word on whether OTP is needed for *this* attempt.
      // Replaces the older client-side trust-window heuristic — after the first
      // /registrations succeeds, the next preflight typically returns
      // `patient_session_already_active` and the order goes straight through.
      //
      // Branches:
      //   - patient_session_already_active → skip OTP, invoke `onSubmit`
      //   - registration_required          → open the NewUser OTP overlay
      //   - verification_required          → switch to HasAccountOTP
      //   - verification_session_already_active → recover token, HasAccountOTP
      //
      // No `step="VerifyingPhone"` flicker: we keep the current step until the
      // response lands, so the user stays on whichever form they were filling.
      if (!voucherDistribution?.slug || !currentPhone) return;
      const slug = voucherDistribution.slug;
      const { dialCode, phone } = currentPhone;
      setPhoneError(null);
      setOtpError(null);

      verifyPhone(
        { slug, configName, dialCode, phone },
        {
          onSuccess: (data) => {
            switch (data.status) {
              case "patient_session_already_active":
                setUserData(mapPatientToUserData(data.patient));
                setStep("DataLoaded");
                // REV-168: see submitPhone branch above — same idempotent guard.
                // Covers the post-/registrations path: form submit → preflight verify →
                // backend says session is already active → we skip OTP but must still
                // emit verification_success so user_type lands on add_payment_info.
                if (!readGaUserType()) {
                  const patientUuid = data.patient?.uuid ?? null;
                  // REV-170: see submitPhone branch — propagate to lastPageView so the next
                  // page_view carries user_id.
                  // REV-211: same for user_ordered_visits (?? 0 — null for restricted_app_access).
                  const userOrderedVisits = data.patient?.visitsCount ?? 0;
                  const lastPageView = getLastPageView();
                  if (lastPageView) {
                    setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
                  }
                  safeTrackEvent(
                    () => verificationSuccessEvent({ userId: patientUuid, method: "account" }),
                    "verification_success",
                    "use-phone-checkout-flow preflightVerifyForSubmit patient_session_already_active",
                  );
                  writeGaUserType("existing");
                }
                onSubmit();
                break;
              case "registration_required":
                triggerNewUserOtpOverlay();
                break;
              case "verification_required":
                writeStoredVerificationToken(slug, dialCode, phone, data.token);
                setVerificationToken(data.token);
                setStep("HasAccountOTP");
                startOtpCooldown();
                break;
              case "verification_session_already_active": {
                const recoveredToken = data.token ?? readStoredVerificationToken(slug, dialCode, phone);
                if (recoveredToken) {
                  writeStoredVerificationToken(slug, dialCode, phone, recoveredToken);
                }
                setVerificationToken(recoveredToken);
                setIsExistingVerificationSession(true);
                setStep("HasAccountOTP");
                break;
              }
            }
          },
          onError: (error) => {
            setPhoneError(translateError(error, "PHONE_AUTH", "VOUCHERS_DISTRIBUTION.ERRORS.PHONE_AUTH.GENERAL_ERROR"));
          },
        },
      );
    },
    [
      voucherDistribution?.slug,
      currentPhone,
      configName,
      verifyPhone,
      triggerNewUserOtpOverlay,
      startOtpCooldown,
      translateError,
      getLastPageView,
      setLastPageView,
    ],
  );

  const submitNewUserOtpOverlay = useCallback(
    (code: string, onSuccessCallback?: () => void) => {
      // Stash the OTP code for the form's submit handler — POST /registrations verifies it
      // atomically with the new account, so we don't dispatch the OTP separately here.
      // Intentionally do NOT change `step` here: the overlay should remain visible
      // through registration → order → payment redirect, so the user perceives a
      // single continuous "confirming…" affordance instead of an overlay flicker.
      if (!verificationToken) {
        // Reached when the overlay opened on an `already_active` 409 with no recoverable
        // token. Letting the form submit fires /registrations with an empty token, which
        // the backend rejects as `invalid_code` — misleading. Surface the real reason.
        setOtpError(t("VOUCHERS_DISTRIBUTION.ERRORS.OTP.SESSION_NOT_RECOVERABLE"));
        return;
      }
      setOtpError(null);
      setPendingVerificationCode(code);
      onSuccessCallback?.();
    },
    [verificationToken, t],
  );

  const closeNewUserOtpOverlay = useCallback(() => {
    // Surface the patient form (and its apiError banner) when something downstream
    // of a successful OTP submit fails — otherwise the user is stuck on the overlay
    // with no visible error.
    //
    // The OTP token+code combo was consumed server-side by /registrations, so
    // wipe the local copies — replaying them would land in a "Kod wygasł" limbo
    // with a disabled CTA. The next side-cart CTA click runs preflight verifyPhone
    // which queries backend state and decides whether to re-trigger OTP at all
    // (post-/registrations the response is typically `patient_session_already_active`,
    // so the order goes straight through without OTP).
    setStep((current) => (current === "NewUserOTPOverlay" ? "NewUser" : current));
    setVerificationToken(null);
    setPendingVerificationCode(null);
    setOtpResendCount(0);
    setOtpCooldownEnd(null);
    setOtpError(null);
    setOtpInputResetSignal((s) => s + 1);
    setIsExistingVerificationSession(false);
    setIsSessionExpired(false);
  }, []);

  const handleRegistrationError = useCallback(
    (error: unknown) => {
      // POST /registrations verifies the OTP atomically with the new account, so the
      // only place wrong-code feedback can land for new users is here. Roll the user
      // back to the OTP step and surface the error so the form can re-display it; if
      // it's some other failure, close the overlay so the patient form's apiError
      // mechanism is visible, and hand control back to the caller.
      const code = extractErrorCode(error);
      if (!isOtpErrorCode(code)) {
        setStep((current) => (current === "NewUserOTPOverlay" ? "NewUser" : current));
        return false;
      }
      setOtpError(translateError(error, "OTP", "VOUCHERS_DISTRIBUTION.ERRORS.OTP.INVALID_CODE"));
      setPendingVerificationCode(null);
      setStep("NewUserOTPOverlay");
      setOtpInputResetSignal((s) => s + 1);
      return true;
    },
    [translateError],
  );

  const handleResendOtp = useCallback(() => {
    if (!voucherDistribution?.slug || !verificationToken || otpResendCount >= 3) return;
    const slug = voucherDistribution.slug;
    setOtpError(null);
    // Inlined (rather than a helper) so the closed-over deps (step, isGuestCheckout) are visible
    // to React's exhaustive-deps lint — a helper sitting outside the useCallback would force us to
    // either memoize it via its own useCallback or drop it from deps and risk a stale closure.
    const method: "account" | "guest" | "sign_up" | null =
      step === "HasAccountOTP"
        ? "account"
        : step === "NewUserOTPOverlay"
          ? isGuestCheckout
            ? "guest"
            : "sign_up"
          : null;
    if (method) {
      safeTrackEvent(
        () => verificationResendCodeEvent({ method }),
        "verification_resend_code",
        "use-phone-checkout-flow handleResendOtp",
      );
    }
    resendVerificationCode(
      {
        slug,
        configName,
        token: verificationToken,
      },
      {
        onSuccess: (data) => {
          // Backend rotates the token on each refresh; persist the new one so the
          // verification_session_already_active fallback uses the latest value.
          if (currentPhone) {
            writeStoredVerificationToken(slug, currentPhone.dialCode, currentPhone.phone, data.token);
          }
          setVerificationToken(data.token);
          setOtpResendCount((prev) => prev + 1);
          startOtpCooldown();
        },
        onError: (error) => {
          const code = extractErrorCode(error);
          // Backend reports `not_found` when the underlying TFA session is gone
          // (expired, revoked, completed elsewhere). The current token is dead —
          // drop it so a "Zmień numer" → re-submit can start a fresh session
          // instead of recovering the stale token from localStorage.
          if (code === "not_found" && currentPhone) {
            clearStoredVerificationToken(slug, currentPhone.dialCode, currentPhone.phone);
            setVerificationToken(null);
            setIsSessionExpired(true);
          }
          setOtpError(translateError(error, "OTP", "VOUCHERS_DISTRIBUTION.ERRORS.OTP.RESEND_FAILED"));
        },
      },
    );
  }, [
    voucherDistribution?.slug,
    configName,
    verificationToken,
    otpResendCount,
    currentPhone,
    resendVerificationCode,
    startOtpCooldown,
    translateError,
    step,
    isGuestCheckout,
  ]);

  const consumeVerificationToken = useCallback(() => {
    // Called by SharedCheckout from registerPatient.onSuccess. The OTP token+code
    // combo was just redeemed atomically with /registrations, so the localStorage
    // copy is dead — drop it so a future visit doesn't try to replay it.
    if (!voucherDistribution?.slug || !currentPhone) return;
    clearStoredVerificationToken(voucherDistribution.slug, currentPhone.dialCode, currentPhone.phone);
  }, [voucherDistribution?.slug, currentPhone]);

  const changePhone = useCallback(() => {
    // Intentionally NOT clearing localStorage here. The stored token is keyed by
    // (slug, dialCode, phone); switching to a different number gets its own entry.
    // If the user re-submits the same number, we still need the token to recover
    // the existing 2FA session (backend won't issue a fresh one while it's open).
    setStep("PhoneInput");
    setVerificationToken(null);
    setPendingVerificationCode(null);
    setUserData(null);
    setCurrentPhone(null);
    setOtpResendCount(0);
    setOtpCooldownEnd(null);
    setOtpError(null);
    setPhoneError(null);
    setIsExistingVerificationSession(false);
    setIsSessionExpired(false);
    setOtpInputResetSignal(0);
    setCustomerData({} as never);
    // User left the form back to PhoneInput; the next NewUser landing should re-fire sign_up_view.
    signUpViewFiredRef.current = false;
  }, [setCustomerData]);

  return {
    step,
    verificationToken,
    pendingVerificationCode,
    userData,
    currentPhone,
    otpResendCount,
    otpCooldownEnd,
    otpError,
    otpInputResetSignal,
    phoneError,
    isExistingVerificationSession,
    isSessionExpired,
    isNewUserOtpVerified: pendingVerificationCode !== null,
    isPreflightLoading: isVerifyPhoneLoading,
    isLoading:
      isVerifyPhoneLoading ||
      isPatientAuthenticationLoading ||
      isResendOtpLoading ||
      isCreateVerificationSessionLoading,
    isPhoneLookupLoading: isVerifyPhoneLoading,
    isVerifyOtpLoading: isPatientAuthenticationLoading,
    isResendOtpLoading,
    submitPhone,
    submitOtp,
    triggerNewUserOtpOverlay,
    submitNewUserOtpOverlay,
    closeNewUserOtpOverlay,
    handleRegistrationError,
    changePhone,
    resendOtp: handleResendOtp,
    consumeVerificationToken,
    preflightVerifyForSubmit,
  };
};
